import type { QuestTemplateType, UserQuestDto } from '@/queries/userQuest/userQuest.schema';
import { ALL_GAMES_CATEGORY_ID } from '@/consts/categoriesIds';
import { AppRoutes } from '@/i18n/constants';
import openModal from '@/modals/utils/openModal';
import { pad } from '@/utils/parsing.utils';
import { buildMissionGamesFilters, buildMissionGamesSearchParams } from '../../../utils/missionGamesFilters';

export type MissionLinkTarget = 'lobby' | 'shop' | 'redeem' | 'daily-rewards';

/** Per-target click handlers that replace the default link / modal (e.g. in the mobile gamification bar). */
export type MissionTargetActions = Partial<Record<MissionLinkTarget, VoidFunction>>;

export const MISSION_LINK_TARGET_BY_TYPE: Partial<Record<QuestTemplateType, MissionLinkTarget>> = {
  'one-bet': 'lobby',
  'one-bet-over': 'lobby',
  'n-bets-in-range': 'lobby',
  'n-bets-over': 'lobby',
  'any-bets': 'lobby',
  'purchase': 'shop',
  'withdrawal': 'redeem',
  'daily-bonus-claim': 'daily-rewards',
};

export function getMissionHref(task: UserQuestDto): string | undefined {
  const missionGamesFilters = buildMissionGamesFilters(task.quest.filters);
  const missionGamesSearchParams = buildMissionGamesSearchParams(missionGamesFilters).toString();

  if (missionGamesSearchParams) {
    return `${AppRoutes.gamificationMissionGames}?${missionGamesSearchParams}`;
  }

  switch (MISSION_LINK_TARGET_BY_TYPE[task.quest.questTemplate.type]) {
    case 'lobby':
      return AppRoutes.categoryId(ALL_GAMES_CATEGORY_ID);
    case 'daily-rewards':
      return AppRoutes.gamificationRewards;
    default:
      return undefined;
  }
}

const MISSION_ACTION_BY_TARGET: MissionTargetActions = {
  shop: () => openModal('deposit'),
  redeem: () => openModal('redeem'),
};

export function getMissionAction(task: UserQuestDto): VoidFunction | undefined {
  const target = MISSION_LINK_TARGET_BY_TYPE[task.quest.questTemplate.type];
  return target && MISSION_ACTION_BY_TARGET[target];
}

export function formatTimeLeft(timeComponents: { hours: number; minutes: number; seconds: number } | null) {
  if (!timeComponents) {
    return undefined;
  }

  const { hours, minutes, seconds } = timeComponents;
  const days = Math.floor(hours / 24);
  const remainingHours = hours % 24;

  if (days > 0) {
    const daysString = `${days}d`;
    const hoursString = remainingHours > 0 && `${remainingHours}h`;
    const minutesString = minutes && `${minutes}m`;
    return [daysString, hoursString, minutesString]
      .filter(Boolean)
      .join(' ');
  }

  return hours > 0
    ? `${pad(hours)}:${pad(minutes)}`
    : `${pad(minutes)}:${pad(seconds)}`;
}
